import { useState, useContext } from 'react';
import { Box, Button, Badge, Link } from '@mui/material';
import { FilterList as FilterIcon } from '@mui/icons-material';
import { format, addDays, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTable, SttChipUser } from '../../../sporttia/all';
import BookingDialog from '../../../components/dialogs/bookingDialog/BookingDialog';
import DateBuilder from '../../../lib/DateBuilder';
import MshipBookingsFilterDialog from './MshipBookingsFilterDialog';

type MshipData = {
    id: number;
};

type Props = {
    mship: MshipData;
};

type FilterState = {
    ini: string;
    end: string;
    source: string | null;
};

type PaginationParams = {
    page: number;
    rows?: number;
};

type BookingData = {
    id: number;
    created: string;
    creator: {
        login: string;
    };
    user: {
        name: string;
    };
    ini: string;
    end: string;
    facility: {
        name: string;
    };
    price: number;
};

type BookingsResponse = {
    rows?: BookingData[];
    total?: number;
};

export default function MshipBookings({ mship }: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [bookings, setBookings] = useState<BookingsResponse>({});
    const [filter, setFilter] = useState<FilterState>({
        ini: format(new Date(), 'yyyy-MM-dd'),
        end: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
        source: null,
    });
    const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);
    const [dialogBookingId, setDialogBookingId] = useState<number | null>();

    /**
     * Get bookings
     */
    const loadBookings = (
        p: PaginationParams = { page: 1, rows: 20 },
        filterOverride?: FilterState,
    ) => {
        const activeFilter = filterOverride ?? filter;
        cxt?.api('GET', `/mships/${mship.id}/bookings`, {
            params: {
                page: p.page,
                rows: p.rows,
                ini: format(parseISO(activeFilter.ini), 'yyyy-MM-dd'),
                end: format(parseISO(activeFilter.end), 'yyyy-MM-dd'),
                source: activeFilter.source,
            },
            success: (r: any) => {
                setBookings(r);
            },
        });
    };

    /**
     * Open filter dialog
     */
    const openFilterDialog = () => {
        setFilterDialogOpen(true);
    };

    /**
     * Apply filters
     */
    const applyFilters = (newFilters: FilterState) => {
        setFilter(newFilters);
        loadBookings({ page: 1, rows: 20 }, newFilters);
    };

    /**
     * Count active filters
     */
    const getActiveFiltersCount = (): number => {
        let count = 0;
        if (filter.source) count++;
        // Check if period is different from default
        const defaultIni = format(new Date(), 'yyyy-MM-dd');
        const defaultEnd = format(addDays(new Date(), 7), 'yyyy-MM-dd');
        if (filter.ini !== defaultIni || filter.end !== defaultEnd) count++;
        return count;
    };

    // -----| Render |-----
    return (
        <>
            {/* Filter Button */}
            <Box mb={3} display="flex" justifyContent="flex-start">
                <Badge badgeContent={getActiveFiltersCount()} color="primary">
                    <Button
                        variant="outlined"
                        startIcon={<FilterIcon />}
                        onClick={openFilterDialog}
                        sx={{
                            color: 'var(--brand-text)',
                            borderColor: 'primary.dark',
                            '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'rgba(186, 242, 0, 0.04)',
                            },
                        }}
                    >
                        {t('generic.filters')}
                    </Button>
                </Badge>
            </Box>

            {/* Filter Dialog */}
            <MshipBookingsFilterDialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                onApply={applyFilters}
                initialFilters={filter}
            />

            <SttTable
                data={bookings}
                columns={[
                    {
                        field: 'id',
                        title: '',
                        width: 60,
                        value: (r: BookingData) => (
                            <Link
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDialogBookingId(r.id);
                                }}
                                sx={{
                                    color: 'var(--brand-text)',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                #{r.id}
                            </Link>
                        ),
                    },
                    {
                        field: 'created',
                        title: t('Created'),
                        width: 140,
                        value: (r: BookingData) => (
                            <>
                                <SttChipUser user={r.creator} />
                                <Box mt={1}>
                                    {new DateBuilder(
                                        r?.created,
                                        cxt?.sc?.timezone?.name,
                                        cxt?.lang.key,
                                    ).dmyhm()}
                                </Box>
                            </>
                        ),
                    },
                    {
                        field: 'created',
                        title: t('Modified'),
                        value: (r: BookingData) =>
                            new DateBuilder(
                                r?.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang.key,
                            ).dmy(),
                    },
                    {
                        field: 'user.name',
                        title: t('User'),
                        value: (r: BookingData) => r.user.name,
                    },
                    { field: 'ini', title: t('Date'), type: 'dmy' },
                    {
                        field: 'time',
                        title: t('Time'),
                        value: (r: BookingData) => ({ ini: r.ini, end: r.end }),
                        type: 'periodTime',
                    },
                    {
                        field: 'facility',
                        title: t('Facility'),
                        value: (r: BookingData) => r.facility.name,
                    },
                    { field: 'price', title: t('Total'), type: 'price' },
                ]}
                onFetch={loadBookings}
            />

            <BookingDialog
                idBooking={dialogBookingId as any}
                onClose={() => setDialogBookingId(null)}
            />
        </>
    );
}
